// NEW_FILE_JUSTIFIED: Settings component for connecting Gmail account via OAuth — mirrors TelegramPersonalConnect pattern
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useAction } from 'convex/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: _generated is the only allowed convex import from src/components
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import type { TGmailConnectStep } from '@/@types/gmail';

export function GmailConnect() {
  const session = useQuery(api.gmail.queries.getMyGmailSession, {});
  const initiateConnectAction = useAction(api.gmail.sessions.initiateConnect);
  const disconnectAction = useAction(api.gmail.sessions.disconnect);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Derive OAuth result from URL params during render (pure derivation, no setState needed)
  const oauthSuccess = searchParams.get('success') === 'gmail';
  const oauthError = searchParams.get('error');

  // Derive initial step and error from URL params so we never need an effect to set them.
  const [step, setStep] = useState<TGmailConnectStep>(() =>
    oauthSuccess ? 'connected' : 'disconnected'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(() => {
    if (oauthError !== null) {
      try {
        return decodeURIComponent(oauthError);
      } catch {
        return oauthError;
      }
    }
    return null;
  });

  // Clean up the OAuth redirect params from the URL after mount.
  // This effect only calls navigate — no setState — so it does not trigger cascading renders.
  useEffect(() => {
    if (oauthSuccess || oauthError !== null) {
      navigate('/connected-accounts', { replace: true });
    }
  }, [oauthSuccess, oauthError, navigate]);

  const handleConnect = useCallback(async () => {
    setErrorMessage(null);
    setStep('connecting');
    try {
      const { authUrl } = await initiateConnectAction({});
      // Redirect to Google OAuth consent screen
      window.location.href = authUrl;
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to start Gmail connection');
      setStep('disconnected');
    }
  }, [initiateConnectAction]);

  const handleDisconnect = useCallback(async () => {
    setErrorMessage(null);
    try {
      await disconnectAction({});
      setStep('disconnected');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Disconnect failed');
    }
  }, [disconnectAction]);

  // Derive displayed state from Convex session + local step
  const isConnected = session?.status === 'active' || step === 'connected';
  const isLoading = step === 'connecting' || session === undefined;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
          <Mail size={18} className="text-muted-foreground" aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">Gmail</h3>
          <p className="text-xs text-muted-foreground">
            {isConnected && session?.emailAddress
              ? session.emailAddress
              : 'Connect your Gmail to send and receive emails'}
          </p>
        </div>

        {/* Status indicator */}
        {isConnected && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
            <CheckCircle size={13} aria-hidden="true" />
            <span>Connected</span>
          </div>
        )}
      </div>

      <Separator />

      {/* Error — always rendered so live region is registered before announcement */}
      <p
        role="alert"
        className={cn(
          'rounded-lg px-3 py-2 text-xs text-destructive',
          errorMessage ? 'bg-destructive/10' : 'sr-only'
        )}
      >
        {errorMessage ?? ''}
      </p>

      {/* State: Connected */}
      {isConnected && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Connected as {session?.emailAddress ?? 'your Gmail account'}
          </p>
          <Button variant="outline" size="sm" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </div>
      )}

      {/* State: Disconnected — show connect button */}
      {!isConnected && !isLoading && (
        <Button onClick={handleConnect} className="w-full">
          <Mail size={16} className="mr-2" aria-hidden="true" />
          Connect Gmail Account
        </Button>
      )}

      {/* State: Loading / Connecting */}
      {isLoading && !isConnected && (
        <div className="flex items-center justify-center gap-2 py-3">
          <Loader2 size={16} className="animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="text-sm text-muted-foreground">Connecting to Gmail...</span>
        </div>
      )}
    </div>
  );
}
